import { FC, useState } from 'react';

import { Link, usePage } from '@inertiajs/react';
import { MessageSquareText, PackageSearch, ShoppingCart } from 'lucide-react';

import { ReviewOrderCard } from './ReviewOrderCard';
import { ReviewsTab, ReviewTabs } from './ReviewTabs';
import { Button } from '../../../components/ui/Button';
import { Routes } from '../../../models/routes';
import { OfferProperty, Order } from '../../../types';
import { OrderReviewDialog } from '../Orders/OrderReviewDialog';
import { Pagination } from '../Orders/Pagination';

type PageProps = {
    orders: Order[] | null;
    activeTab: ReviewsTab;
    counts: Record<ReviewsTab, number>;
    offerProperties: OfferProperty[];
    nextPage: number | null;
};

const EmptyState: FC<{ activeTab: ReviewsTab }> = ({ activeTab }) => {
    const pending = activeTab === 'pending';

    return (
        <div className="rounded-2xl border bg-muted/10 px-4 py-12 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                {pending ? (
                    <PackageSearch className="h-6 w-6 text-muted-foreground" />
                ) : (
                    <MessageSquareText className="h-6 w-6 text-muted-foreground" />
                )}
            </div>
            <h2 className="text-lg font-medium">
                {pending
                    ? 'Немає замовлень, які очікують відгуку'
                    : 'Ви ще не залишали відгуків'}
            </h2>
            <p className="mx-auto mt-1 max-w-lg text-sm text-muted-foreground">
                {pending
                    ? 'Тут з’являться завершені замовлення, для яких ще можна оцінити продавця.'
                    : 'Після завершення покупки ви зможете поділитися враженнями про продавця.'}
            </p>
            {pending && (
                <Button asChild className="mt-5">
                    <Link href={Routes.Purchases}>
                        <ShoppingCart className="mr-2 h-4 w-4" />
                        Перейти до покупок
                    </Link>
                </Button>
            )}
        </div>
    );
};

const Reviews: FC = () => {
    const { orders, activeTab, counts, offerProperties, nextPage } =
        usePage<PageProps>().props;
    const [reviewOrder, setReviewOrder] = useState<Order | null>(null);
    const ordersList = orders || [];

    return (
        <>
            <OrderReviewDialog
                order={reviewOrder}
                open={Boolean(reviewOrder)}
                source="profile_reviews"
                onOpenChange={(open) => {
                    if (!open) setReviewOrder(null);
                }}
            />

            <div className="space-y-6 py-6">
                <div>
                    <h1 className="text-2xl font-bold sm:text-3xl">Відгуки</h1>
                </div>

                <ReviewTabs activeTab={activeTab} counts={counts} />

                {ordersList.length === 0 ? (
                    <EmptyState activeTab={activeTab} />
                ) : (
                    <div className="space-y-4">
                        {ordersList.map((order) => (
                            <ReviewOrderCard
                                key={order.id}
                                order={order}
                                offerProperties={offerProperties}
                                onReview={setReviewOrder}
                            />
                        ))}
                    </div>
                )}

                {ordersList.length > 0 && (
                    <Pagination
                        url={Routes.Reviews}
                        nextPage={nextPage}
                        params={{ tab: activeTab }}
                    />
                )}
            </div>
        </>
    );
};

export default Reviews;
